// Sign-in (both steps) and two-step verification: the login card, enrollment (login and Akun) and recovery codes.
import { PASSWORD_MIN_LENGTH, TOTP_CODE_LENGTH } from '@constants/limits';

export const LOGIN = {
  title: 'Login',
  system: (appName: string) => `Sistem ${appName}`,
  emblemAlt: 'Kementerian Energi dan Sumber Daya Mineral',
  email: 'Email',
  emailPlaceholder: 'nama@perusahaan.co.id',
  emailRequired: 'Email wajib diisi.',
  emailInvalid: 'Format email belum benar, contoh: nama@perusahaan.co.id',
  password: 'Kata sandi',
  passwordPlaceholder: 'Masukkan kata sandi',
  passwordRequired: 'Kata sandi wajib diisi.',
  capsLock: 'Caps Lock aktif.',
  forgot: 'Lupa kata sandi?',
  forgotHelp: 'Kata sandi tidak dapat dipulihkan sendiri. Minta administrator membuatkan kata sandi baru dari halaman Pengguna.',
  submit: 'Masuk',
  copyright: (year: string, holder: string) => `© ${year} ${holder}. Hak cipta dilindungi undang-undang.`,
  /** the pending second step ran out (a few minutes) or was replaced by a sign-in in another tab */
  expired: 'Waktu verifikasi habis. Masuk lagi dengan email dan kata sandi.',
} as const;

/** The code field (sign-in, enrollment, disabling, new recovery codes). */
export const CODE = {
  label: 'Kode verifikasi',
  help: `${TOTP_CODE_LENGTH} digit dari aplikasi autentikator.`,
  required: `Isi ${TOTP_CODE_LENGTH} digit kode verifikasi.`,
  short: `Kode verifikasi terdiri dari ${TOTP_CODE_LENGTH} digit.`,
  recoveryLabel: 'Kode pemulihan',
  recoveryHelp: 'Salah satu kode pemulihan yang Anda simpan saat mengaktifkan verifikasi dua langkah. Setiap kode hanya berlaku sekali.',
  recoveryRequired: 'Isi kode pemulihan.',
} as const;

/** The sign-in's second step. */
export const VERIFY = {
  title: 'Verifikasi dua langkah',
  text: 'Masukkan kode verifikasi dari aplikasi autentikator di ponsel Anda.',
  textRecovery: 'Masukkan salah satu kode pemulihan Anda.',
  submit: 'Verifikasi',
  useRecovery: 'Gunakan kode pemulihan',
  useApp: 'Gunakan kode dari aplikasi',
  back: 'Masuk dengan akun lain',
  lostHelp: 'Tidak memiliki akses ke aplikasi maupun kode pemulihan? Minta administrator mengatur ulang verifikasi dua langkah akun Anda.',
  lost: 'Tidak dapat mengakses aplikasi?',
} as const;

export const AUTHENTICATOR_APPS = 'Google Authenticator atau Microsoft Authenticator';

/** Turning two-step verification on: the forced flow at sign-in, the gate and the Akun dialog. */
export const ENROLL = {
  title: 'Aktifkan verifikasi dua langkah',
  requiredTitle: 'Verifikasi dua langkah wajib',
  requiredText: 'Akun Anda wajib memakai verifikasi dua langkah. Selesaikan langkah berikut untuk melanjutkan.',
  intro: 'Setiap kali masuk, selain kata sandi Anda memasukkan kode dari aplikasi autentikator di ponsel.',
  /** enabling from Akun signs the other devices out */
  introSignOut: 'Perangkat lain yang sedang masuk akan dikeluarkan setelah verifikasi dua langkah aktif.',
  stepsLabel: 'Langkah aktivasi',
  step1: 'Pasang aplikasi autentikator',
  step1Text: `Pasang ${AUTHENTICATOR_APPS} dari toko aplikasi ponsel Anda.`,
  step2: 'Pindai kode QR',
  step2Text: 'Di aplikasi, pilih tambah akun, lalu pindai kode QR ini.',
  qrLabel: 'Kode QR untuk aplikasi autentikator',
  manual: 'Tidak dapat memindai? Masukkan kunci ini secara manual:',
  secretLabel: 'Kunci pengaturan',
  copySecret: 'Salin kunci',
  secretCopied: 'Kunci disalin',
  secretCopyFailed: 'Tidak dapat menyalin otomatis. Pilih kunci lalu salin secara manual.',
  step3: 'Masukkan kode dari aplikasi',
  step3Text: 'Aplikasi menampilkan kode 6 digit yang berganti setiap 30 detik.',
  passwordHelp: 'Untuk memastikan perubahan ini dilakukan oleh Anda.',
  submit: 'Aktifkan',
  preparing: 'Menyiapkan kunci',
  setupFailed: 'Kunci verifikasi tidak dapat disiapkan.',
  cancel: 'Batal',
  signOut: 'Keluar',
  signInAgain: 'Masuk lagi',
  enabled: 'Verifikasi dua langkah aktif',
  /** enrolled at sign-in, but the server did not open the session: what to do next */
  enabledLogin: 'Verifikasi dua langkah sudah aktif. Masuk lagi dengan kata sandi, lalu kode dari aplikasi.',
} as const;

/** Recovery codes: shown once after enabling or regenerating. */
export const RECOVERY = {
  title: 'Simpan kode pemulihan',
  text: 'Gunakan kode ini untuk masuk bila ponsel Anda hilang atau aplikasi autentikator tidak dapat dibuka. Setiap kode hanya berlaku sekali.',
  once: 'Kode ini hanya ditampilkan sekali. Simpan di tempat yang aman, misalnya pengelola kata sandi atau dokumen cetak yang disimpan terkunci.',
  listLabel: 'Kode pemulihan',
  copy: 'Salin',
  copied: 'Kode pemulihan disalin',
  copyFailed: 'Tidak dapat menyalin otomatis. Pilih kode lalu salin secara manual, atau unduh sebagai berkas.',
  download: 'Unduh (.txt)',
  downloaded: 'Berkas kode pemulihan diunduh',
  confirm: 'Saya sudah menyimpan kode ini',
  confirmNeeded: 'Centang konfirmasi setelah kode disimpan.',
  done: 'Lanjutkan',
  fileName: 'kode-pemulihan.txt',
  fileHeader: (app: string, email: string) => [`Kode pemulihan verifikasi dua langkah: ${app}`, `Akun: ${email}`],
  fileCreated: (when: string) => `Dibuat: ${when}`,
  fileNote: 'Setiap kode hanya berlaku sekali. Simpan berkas ini di tempat yang aman.',
} as const;

/** Changing one's own password (Akun dialog), strength hints included. */
export const PASSWORD = {
  current: 'Kata sandi saat ini',
  next: 'Kata sandi baru',
  again: 'Ulangi kata sandi baru',
  currentRequired: 'Isi kata sandi Anda saat ini.',
  nextRequired: 'Isi kata sandi baru.',
  againRequired: 'Ulangi kata sandi baru.',
  tooShort: `Minimal ${PASSWORD_MIN_LENGTH} karakter.`,
  tooLong: (max: number) => `Maksimal ${max} karakter.`,
  same: 'Kata sandi baru tidak boleh sama dengan kata sandi saat ini.',
  mismatch: 'Belum sama dengan kata sandi baru.',
  wrongCurrent: 'Kata sandi saat ini salah.',
  capsLockHint: 'Caps Lock aktif.',
  show: 'Tampilkan kata sandi',
  hide: 'Sembunyikan kata sandi',
  strengthIdle: `Minimal ${PASSWORD_MIN_LENGTH} karakter. Makin panjang makin aman.`,
  strength: 'Kekuatan:',
  levels: { 1: 'Lemah', 2: 'Cukup', 3: 'Kuat', 4: 'Sangat kuat' },
  levelShort: 'Terlalu pendek',
  tipCommon: 'Hindari kata atau urutan yang mudah ditebak.',
  tipKinds: 'Campur huruf besar, angka, atau simbol.',
  tipLonger: 'Makin panjang makin aman.',
} as const;
